import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, FolderOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const CategoryManager = ({ open, onOpenChange, language = 'es' }) => {
  const [categories, setCategories] = useState({ regular: [], sensitive: [] });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState('regular');

  const t = {
    es: {
      title: 'Gestionar Categorías',
      newCategory: 'Nueva categoría...',
      add: 'Añadir',
      regular: 'Regulares',
      sensitive: 'Sensibles',
      loadDefaults: 'Cargar categorías predeterminadas',
      noCategories: 'Sin categorías. Carga las predeterminadas.',
      close: 'Cerrar',
      created: 'Categoría creada',
      deleted: 'Categoría eliminada',
      nameRequired: 'Nombre de categoría es obligatorio',
    },
    en: {
      title: 'Manage Categories',
      newCategory: 'New category...',
      add: 'Add',
      regular: 'Regular',
      sensitive: 'Sensitive',
      loadDefaults: 'Load default categories',
      noCategories: 'No categories. Load defaults.',
      close: 'Close',
      created: 'Category created',
      deleted: 'Category deleted',
      nameRequired: 'Category name is required',
    }
  };

  const texts = t[language] || t.es;

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      const cats = response.data || [];
      setCategories({
        regular: cats.filter(c => c.type === 'regular'),
        sensitive: cats.filter(c => c.type === 'sensitive')
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    if (open) {
      // Use IIFE to properly handle async within useEffect
      (async () => {
        try {
          const response = await axios.get(`${API}/admin/categories`);
          const cats = response.data || [];
          setCategories({
            regular: cats.filter(c => c.type === 'regular'),
            sensitive: cats.filter(c => c.type === 'sensitive')
          });
        } catch (error) {
          console.error('Error fetching categories:', error);
        }
      })();
    }
  }, [open]);

  const handleCreate = async () => {
    if (!newCategoryName.trim()) {
      toast.error(texts.nameRequired);
      return;
    }
    try {
      await axios.post(`${API}/admin/categories`, { 
        name: newCategoryName, 
        type: newCategoryType 
      });
      toast.success(texts.created);
      setNewCategoryName('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success(texts.deleted);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleSeed = async () => {
    try {
      const response = await axios.post(`${API}/admin/categories/seed`);
      toast.success(response.data.message);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            {texts.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Add new category */}
          <div className="flex gap-2">
            <Input
              placeholder={texts.newCategory}
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <Select value={newCategoryType} onValueChange={setNewCategoryType}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="regular">Regular</SelectItem>
                <SelectItem value="sensitive">{texts.sensitive}</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-1" /> {texts.add}
            </Button>
          </div>

          <Button variant="outline" size="sm" onClick={handleSeed}>
            {texts.loadDefaults}
          </Button>

          <div className="grid grid-cols-2 gap-4">
            {/* Regular Categories */}
            <div>
              <h4 className="font-medium mb-2">
                {texts.regular} ({categories.regular.length})
              </h4>
              <div className="space-y-1 max-h-60 overflow-y-auto border rounded p-2">
                {categories.regular.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between py-1 px-2 bg-slate-50 rounded">
                    <span className="text-sm">{cat.name}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700" 
                      onClick={() => handleDelete(cat.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {categories.regular.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {texts.noCategories}
                  </p>
                )}
              </div>
            </div>

            {/* Sensitive Categories */}
            <div>
              <h4 className="font-medium mb-2 text-orange-600">
                {texts.sensitive} ({categories.sensitive.length})
              </h4>
              <div className="space-y-1 max-h-60 overflow-y-auto border border-orange-200 rounded p-2">
                {categories.sensitive.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between py-1 px-2 bg-orange-50 rounded">
                    <span className="text-sm">{cat.name}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700" 
                      onClick={() => handleDelete(cat.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {categories.sensitive.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {texts.noCategories}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {texts.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryManager;
